// Сквозные отчёты: обращение к своему разделу ядра через BFF.
//
// Устроено как api/nl.ts и api/p73x.ts: свой БАЗА, своя функция запроса, хуки рядом.
// Общего клиента здесь нет намеренно — api/client.ts обслуживает эмуляцию Bitrix REST
// (POST /api/bx), а у модулей ядра форма ответа другая.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import type {
  Список, СправочникиXR, СтрокаБанк, СтрокаДеньги, СтрокаЗакупка, СтрокаПоставщик,
  СтрокаРаботы, ХвостОтчёта,
} from '../lib/xreports'

const БАЗА = '/api/xreports'

/** Тот же разбор ответа, что в api/client.ts, api/nl.ts и api/p73x.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 здесь бывает не только от кривого фильтра: ядро отвечает им и на «окно отчёта
    // пусто, судить не о чем». Это надо ОБЪЯСНИТЬ, а не показать пустую таблицу —
    // пустая таблица в разделе про деньги читается как «нарушений нет».
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

export interface ФильтрыXR {
  [ключ: string]: unknown
  q?: string
  from?: string
  to?: string
  deal?: number
  match?: string
  state?: string
  status?: string
  aged?: boolean
  start?: number
  limit?: number
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// Отчёты пересчитываются раз в несколько минут вместе с синками — держать их «свежими»
// секундным поллингом нечего. Отдельно про bank-invoice: его подсчёт стоит около полутора
// секунд в ядре (индекса по номеру счёта на боевой базе нет), поэтому у него staleTime
// больше — иначе переключение вкладок туда-обратно каждый раз занимало бы соединение ядра.
const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const
const ДОРОГОЕ = { staleTime: 5 * 60_000, retry: 1 } as const

export function useXRSprav() {
  return useQuery<СправочникиXR>({
    queryKey: ['xreports', 'refs'],
    queryFn: () => запрос<СправочникиXR>('/refs'),
    ...ОБЩЕЕ,
  })
}

type Ответ<T> = Список<T> & ХвостОтчёта

export function useБанкПротивРеестра(ф: ФильтрыXR, включён = true) {
  return useQuery<Ответ<СтрокаБанк>>({
    queryKey: ['xreports', 'bank-invoice', ф],
    queryFn: () => запрос('/bank-invoice' + строкаЗапроса(ф)),
    enabled: включён,
    ...ДОРОГОЕ,
  })
}

export function useПоставщикБезЗакрывающих(ф: ФильтрыXR, включён = true) {
  return useQuery<Ответ<СтрокаПоставщик> & { hang_days: number }>({
    queryKey: ['xreports', 'supplier-open', ф],
    queryFn: () => запрос('/supplier-open' + строкаЗапроса(ф)),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}

export function useЗакупкаБезПрихода(ф: ФильтрыXR, включён = true) {
  return useQuery<Ответ<СтрокаЗакупка> & {
    by_currency: { currency_ref: string; n: number; amount: number; n_aged: number }[]
    main_currency_ref: string | null
  }>({
    queryKey: ['xreports', 'supply-late', ф],
    queryFn: () => запрос('/supply-late' + строкаЗапроса(ф)),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}

export function useРаботыБезСчёта(ф: ФильтрыXR, включён = true) {
  return useQuery<Ответ<СтрокаРаботы>>({
    queryKey: ['xreports', 'works-unbilled', ф],
    queryFn: () => запрос('/works-unbilled' + строкаЗапроса(ф)),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}

export function useДеньгиБезЗакрытия(ф: ФильтрыXR, включён = true) {
  return useQuery<Ответ<СтрокаДеньги>>({
    queryKey: ['xreports', 'money-unclosed', ф],
    queryFn: () => запрос('/money-unclosed' + строкаЗапроса(ф)),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}
